/**
 * Routes that are registered through `router.ts`'s documented extension point
 * rather than by editing its table.
 *
 * `router.ts` states the mechanism at the top of the file: "`routes` is a mutable
 * export: push a RouteConfig onto it and call `router.goto(location.pathname)` —
 * that is the documented extension point for the backoffice console, which owns
 * its own sub-tree." This module uses it for two pages whose absence from the
 * table was a named product gap rather than a styling choice:
 *
 *   - **`/api-reference`** (`SC-F202`) — the in-app viewer for the OpenAPI
 *     document `internal/apidoc` has been serving since MVP.
 *   - **`/device`** (`SC-F217`) — the browser confirmation screen for the CLI's
 *     device authorization flow. `internal/pat`'s `DeviceVerificationPath` is the
 *     path a human is sent to on another machine, printed by
 *     `sluicecloud auth login`; until this route existed, following that URL
 *     landed on the app shell with nothing in it.
 *
 * **No `goto` call is needed here and that is not an oversight.** The pushes below
 * run at module evaluation, and `main.ts` constructs its one `Router` in a class
 * field — which is evaluated when the shell element is constructed, after every
 * module has been evaluated. `@lit-labs/router` copies the array it is handed
 * (`this.routes = [...routes]`), so the copy already contains these two. A push
 * that happened later, from a lazily-imported module, WOULD need the `goto`.
 *
 * **Order.** Pushed routes land at the end of the table, after the parameterized
 * patterns. Both paths here are single literal segments and the table contains no
 * single-segment parameterized route, so nothing above can swallow them.
 * `URLPattern` matching is by pattern, not by registration order, for everything
 * except the first-match rule the table's own comment describes.
 *
 * **The guards differ, and the difference is the security argument.**
 * `/api-reference` is unguarded because the document it renders is served
 * anonymously — guarding a public document would only hide it during the sign-in
 * outage a developer is most likely to be debugging. `/device` is guarded because
 * its entire job is to authenticate a PERSON: `01` §3.4 forbids automation ever
 * minting a step-up grant, and approving a device login mints an API key.
 */

import { html } from "lit";
import { routes, guard } from "../router.js";

import "./page-api-reference.js";
import "./page-device.js";

routes.push(
  {
    // `SC-F202`. **Deliberately UNGUARDED**: `GET /v1/openapi.json` is an
    // anonymous route (`06` §3.1) and the page reads nothing else, so a guard
    // would add a sign-in wall in front of a public document. It shows no
    // customer data — the document describes the surface, not any organization's
    // use of it.
    path: "/api-reference",
    render: () => html`<page-api-reference></page-api-reference>`,
  },
  {
    // `SC-F217`. GUARDED, and the guard is load-bearing rather than cosmetic.
    // The page approves a device login, which causes a long-lived general API
    // key to exist, and `POST /v1/device/approve` demands a session credential
    // plus a fresh step-up grant on top of it. An unguarded page would send an
    // anonymous reader through a lookup and a confirmation dialog only to be
    // refused at the last click; the guard sends them to sign in first and
    // carries the user code back through `?next=`.
    path: "/device",
    enter: guard,
    render: () => html`<page-device></page-device>`,
  },
);
